/**
 * A hero do template: vídeo de fundo, etiqueta, título de cartaz e dois
 * caminhos.
 *
 * A estrutura é a do original — `.home-hero-section` com uma tela inteira de
 * altura, `.hero-block` alinhado à esquerda dentro do wrapper centralizado, e
 * os dois botões numa linha por baixo. O que muda é o que está escrito: a
 * etiqueta anuncia a edição que está de fato com inscrições abertas, não uma
 * "Momentum Cup 2025" fixa no HTML.
 *
 * O vídeo é servido do próprio bundle, e é uma versão reduzida do arquivo que
 * já vivia no projeto: 1280px de largura e 377 KB, no lugar dos 24 MB do
 * original em 4K. O poster são outros 38 KB.
 *
 * Não é só economia de banda — o original de 4K passava do teto de 25 MB por
 * arquivo da Cloudflare e derrubava a publicação inteira. E vale lembrar o que
 * este vídeo é: cenário atrás de um véu escuro, com um título de 90px por cima.
 * Nessas condições ninguém distingue 4K de 720p; o que se percebe é a espera.
 *
 * Os arquivos saem de `tools/` com ffmpeg a partir do master, que continua em
 * `src/assets/` para quando for preciso gerar outra versão.
 */

import { useRef } from 'react';
import { Link } from 'react-router';
import { useEntradaDaHero } from './animacoes';
import { useAuth } from '@/lib/auth';
import { money } from '@/lib/format';
import type { TournamentSummary } from '@/lib/api';
import videoDeFundo from '@/assets/hero.mp4';
import posterDoVideo from '@/assets/hero-poster.jpg';

interface Props {
  /** Edições com inscrições abertas, já ordenadas pela mais próxima. */
  abertas: TournamentSummary[];
  carregando?: boolean;
}

export function HeroTemplate({ abertas, carregando = false }: Props) {
  const raiz = useRef<HTMLDivElement>(null);
  const { isStaff } = useAuth();
  const proxima = abertas[0] ?? null;

  useEntradaDaHero(raiz);

  return (
    <div ref={raiz} className="home-hero-section">
      {/*
        `muted` e `playsInline` não são opcionais: sem os dois, o navegador
        recusa o autoplay e o fundo fica no poster. `aria-hidden` porque o vídeo
        é cenário — não há informação nele que o texto ao lado não dê.
      */}
      <video
        className="hero-video"
        autoPlay
        loop
        muted
        playsInline
        preload="metadata"
        poster={posterDoVideo}
        aria-hidden
      >
        <source src={videoDeFundo} type="video/mp4" />
      </video>

      {/* Véu por cima do vídeo. Sem ele o título de 90px disputa contraste com
          um gramado em movimento, e perde em metade dos quadros. */}
      <div className="hero-veu" aria-hidden />

      <div className="container">
        {/*
          Duas colunas nas telas largas: a chamada à esquerda e, à direita, as
          edições com inscrição aberta — que é o que quem chega na home quer
          saber antes de rolar. Abaixo de 1280px o card sai e a hero volta a
          ser só a chamada centralizada, como no template.
        */}
        <div className="mpc-hero-grade">
        <div className="wrapper home-hero-wrapper">
          <div className="hero-block">
            <div className="momentum-block" data-entra="etiqueta">
              <div>{etiqueta(proxima, carregando)}</div>
            </div>

            {/*
              O título entra por trás de uma máscara — o `overflow: clip` do
              `<span>` de fora é o que faz o texto surgir de baixo em vez de
              aparecer inteiro.

              Duas linhas, e não três. `.tournament-title` tem 32rem de largura
              e o Anton entra a 90px: cada linha comporta duas palavras. Com três
              linhas o bloco inteiro cresce, o wrapper centraliza mais para cima
              e a etiqueta vai parar atrás da pílula da navbar — o mesmo aperto
              que o título original do template ("Tournament of Champions")
              evita por ter o tamanho que tem.
            */}
            <h1 className="tournament-title">
              <span className="titulo-mascara">
                <span data-entra="titulo">A arena do Pro Clubs</span>
              </span>
            </h1>

            <p className="large-paragraph tournament-para" data-entra="texto">
              Sorteio auditável com semente pública, chaveamento automático e estatísticas
              sincronizadas direto do EA SPORTS FC. PS5, Xbox Series X|S e PC.
            </p>
          </div>

          <div className="hero-buttons">
            {/* O botão leva ao cadastro do time, que é o que ele promete. Para
                quem organiza o destino é a lista: staff não se inscreve. */}
            <Link
              to={isStaff ? '/campeonatos' : '/cadastrar-time'}
              className="red-button"
              data-entra="acoes"
            >
              <div className="red-button-text">
                {isStaff ? 'Ver campeonatos' : 'Inscrever meu time'}
              </div>
              <div className="red-button-bg" aria-hidden />
            </Link>

            <Link to="/campeonatos" className="transparent-button" data-entra="acoes">
              <div className="transparent-button-text">Explorar edições</div>
              <div className="transparent-button-bg" aria-hidden />
            </Link>
          </div>
        </div>

          <aside className="mpc-hero-lado mpc-hero-abertas" aria-label="Campeonatos abertos" data-entra="texto">
            <CampeonatosAbertos abertas={abertas} carregando={carregando} staff={isStaff} />
          </aside>
        </div>
      </div>
    </div>
  );
}

/**
 * As edições com inscrição aberta, no canto direito da hero. Até três, a mais
 * próxima primeiro, cada uma com as vagas que sobram e o valor — os dois
 * números que decidem a inscrição. Sem nenhuma aberta, o card diz isso e
 * aponta para o histórico, em vez de sumir e deixar a coluna vazia.
 */
function CampeonatosAbertos({
  abertas,
  carregando,
  staff,
}: {
  abertas: TournamentSummary[];
  carregando: boolean;
  staff: boolean;
}) {
  const lista = abertas.slice(0, 3);
  return (
    <div className="mpc-abertas-card">
      <p className="mpc-abertas-titulo">
        <span className="mpc-abertas-ponto" aria-hidden />
        Campeonatos abertos
      </p>

      {carregando ? (
        <p className="mpc-abertas-vazio">Carregando edições…</p>
      ) : lista.length === 0 ? (
        <>
          <p className="mpc-abertas-vazio">Nenhuma edição com inscrições abertas agora.</p>
          <Link to="/campeonatos" className="mpc-abertas-link">
            Ver edições anteriores →
          </Link>
        </>
      ) : (
        <ul className="mpc-abertas-lista">
          {lista.map((t) => {
            const preenchidas = t.capacity - t.spotsLeft;
            const pct = t.capacity ? Math.round((preenchidas / t.capacity) * 100) : 0;
            return (
              <li key={t.id}>
                <Link to={`/campeonatos/${t.slug}`} className="mpc-abertas-item">
                  <span className="mpc-abertas-nome">{t.name}</span>
                  <span className="mpc-abertas-dados">
                    {t.spotsLeft > 0 ? `${t.spotsLeft} vagas` : 'Lotado'} ·{' '}
                    {t.entryFeeCents > 0 ? money(t.entryFeeCents) : 'Grátis'}
                    {t.prizePoolCents > 0 && <> · prêmio {money(t.prizePoolCents)}</>}
                  </span>
                  <span className="mpc-abertas-barra" aria-hidden>
                    <span style={{ width: `${pct}%` }} />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {!staff && lista.length > 0 && (
        <Link to={`/campeonatos/${lista[0]!.slug}/inscricao`} className="mpc-abertas-cta">
          Inscrever meu time
        </Link>
      )}
    </div>
  );
}

/**
 * O texto da pílula.
 *
 * Com edição aberta, ela anuncia qual é. Sem nenhuma, cai para o jogo em
 * disputa — e nunca inventa uma temporada: uma pílula anunciando "Temporada
 * 2026" numa página sem campeonato nenhum é a primeira coisa que faz o
 * visitante desconfiar do resto.
 */
function etiqueta(proxima: TournamentSummary | null, carregando: boolean): string {
  if (carregando) return 'Carregando edições';
  if (proxima) return `🏆 Inscrições abertas · ${proxima.name}`;
  return '🏆 EA SPORTS FC · Pro Clubs';
}
